/**
 * Provider-agnostic checkout entry point.
 *
 * Reads the active payment provider for the given `kind` from the backend
 * (admin can toggle each flow independently) and dispatches to the correct
 * SDK. Callers pass the same options they used with `openRazorpay` — the
 * only difference is that PhonePe redirects off-page, so `onSuccess` is not
 * called by PhonePe (the /payment-result page handles that).
 */
import { api } from "./api";
import { openRazorpay } from "./razorpay";
import { openPhonePe } from "./phonepe";

// Cache the provider for a short window to avoid hammering the backend on
// pages that render many pay buttons.
const _cache = new Map();
const TTL_MS = 30_000;

async function activeProvider(kind) {
  const cached = _cache.get(kind);
  if (cached && cached.expires > Date.now()) return cached.provider;
  try {
    const { data } = await api.get(`/public/payment-provider?kind=${encodeURIComponent(kind)}`);
    _cache.set(kind, { provider: data.provider, expires: Date.now() + TTL_MS });
    return data.provider;
  } catch (_) {
    return "razorpay";
  }
}

/**
 * Kick off checkout. `opts.kind` decides which provider to route through.
 *  - opts.kind, opts.ref_id, opts.amount, opts.donor, opts.member_id
 *  - opts.onSuccess(txn)   — Razorpay only (modal flow)
 *  - opts.onFailure(err)
 *  - opts.onDismiss()      — Razorpay only
 *  - opts.return_to        — PhonePe only (redirect-back path)
 */
export async function startCheckout(opts) {
  const provider = await activeProvider(opts.kind);
  if (provider === "phonepe") {
    await openPhonePe(opts);
  } else {
    await openRazorpay(opts);
  }
}

export function clearProviderCache() { _cache.clear(); }
